import { useEffect, useState } from "react";

interface Heading {
  id: string;
  text: string;
  level: number;
}

export function TableOfContents({ containerId }: { containerId: string }) {
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeId, setActiveId] = useState<string>("");

  useEffect(() => {
    const container = document.getElementById(containerId);
    if (!container) return;
    const nodes = Array.from(container.querySelectorAll<HTMLHeadingElement>("h2, h3"));
    const items: Heading[] = nodes
      .filter((n) => n.id)
      .map((n) => ({ id: n.id, text: n.textContent ?? "", level: Number(n.tagName[1]) }));
    setHeadings(items);

    if (!items.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length) {
          setActiveId(visible[0]!.target.id);
        }
      },
      { rootMargin: "-80px 0px -70% 0px", threshold: 0 },
    );
    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, [containerId]);

  if (headings.length < 2) return null;

  return (
    <nav aria-label="On this page" className="sticky top-24 text-sm">
      <p className="mb-3 font-semibold uppercase tracking-wider text-bark-600 text-xs">
        On this page
      </p>
      <ul className="space-y-2 border-l border-cream-300">
        {headings.map((h) => (
          <li key={h.id} className={h.level === 3 ? "pl-6" : "pl-4"}>
            <a
              href={`#${h.id}`}
              className={`block border-l-2 -ml-px pl-3 py-0.5 transition-colors ${
                activeId === h.id
                  ? "border-forest-600 text-forest-700 font-medium"
                  : "border-transparent text-bark-700 hover:text-forest-700"
              }`}
            >
              {h.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
